<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useDeviceStore } from '@/views/Devices/store'
import { useUserStore } from '@/views/Companies/store'
import { useLocationsStore } from '@/views/Locations/store'
import { Device } from '@/views/Devices/models/device'
import { Company } from '@/views/Companies/models/company'
import { Locations } from '@/views/Locations/models/locations'

// Stores
const deviceStore = useDeviceStore()
const userStore = useUserStore()
const locationsStore = useLocationsStore()

// Local state
const showCreateDialog = ref(false)
const showEditDialog = ref(false)
const showDeleteDialog = ref(false)
const selectedDevice = ref<Device | null>(null)
const searchQuery = ref('')
const filterCompany = ref('all')
const filterStatus = ref('all')
const itemsPerPage = ref(10)
const currentPage = ref(1)
const loading = ref(false)
const error = ref<string | null>(null)

// Form data
const deviceForm = ref({
  code: '',
  name: '',
  extendedName: '',
  description: '',
  companyId: '',
  locationId: '',
  active: true,
  lat: 0,
  lng: 0,
  altitude: 0,
  periodicity: 30,
  groupId: 1
})
// Helper methods
const getCompanyByLocationId = (locationId: string) => {
  const location = locations.value.find(l => l.id === locationId)
  return location ? companies.value.find(c => c.id === location.companyId.toString()) : null
}

// Get real data from stores
const companies = computed(() => userStore.companies)
const locations = computed(() => locationsStore.locations)

// Get devices from store instead of sample data
const devices = computed(() => deviceStore.devices)

// Dashboard stats computed from real data
const dashboardStats = computed(() => ({
  totalDevices: devices.value.length,
  activeDevices: devices.value.filter(d => d.active).length,
  alarmedDevices: devices.value.filter(d => d.alarmed).length,
  companiesWithDevices: new Set(devices.value.map(d => getCompanyByLocationId(d.locationId)?.id)).size
}))

// Computed properties
const filteredDevices = computed(() => {
  let filtered = devices.value

  // Filter by company
  if (filterCompany.value !== 'all') {
    filtered = filtered.filter(device => {
      const location = locations.value.find(l => l.id === device.locationId)
      return location?.companyId.toString() === filterCompany.value
    })
  }

  // Filter by status
  if (filterStatus.value === 'active') {
    filtered = filtered.filter(d => d.active)
  } else if (filterStatus.value === 'inactive') {
    filtered = filtered.filter(d => !d.active)
  } else if (filterStatus.value === 'alarmed') {
    filtered = filtered.filter(d => d.alarmed)
  }

  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(device => 
      device.name.toLowerCase().includes(query) ||
      device.id.toLowerCase().includes(query) ||
      device.description.toLowerCase().includes(query)
    )
  }

  return filtered
})

const paginatedDevices = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return filteredDevices.value.slice(start, end)
})

const totalPages = computed(() => {
  return Math.ceil(filteredDevices.value.length / itemsPerPage.value)
})

const availableLocations = computed(() => {
  if (deviceForm.value.companyId) {
    return locations.value.filter(l => l.companyId.toString() === deviceForm.value.companyId)
  }
  return locations.value
})



const getLocationById = (locationId: string) => {
  return locations.value.find(l => l.id === locationId)
}

const getStatusColor = (device: Device) => {
  if (!device.active) return 'error'
  if (device.alarmed) return 'warning'
  return 'success'
}

const getStatusText = (device: Device) => {
  if (!device.active) return 'Inactivo'
  if (device.alarmed) return 'Con Alarma'
  return 'Activo'
}

const getStatusIcon = (device: Device) => {
  if (!device.active) return 'mdi-power-off'
  if (device.alarmed) return 'mdi-alert-circle'  
  return 'mdi-check-circle'
}

// Dialog methods
const openCreateDialog = () => {
  resetForm()
  showCreateDialog.value = true
}

const openEditDialog = (device: Device) => {
  selectedDevice.value = device
  deviceForm.value = {
    code: device.code,
    name: device.name,
    extendedName: device.extendedName,
    description: device.description,
    companyId: getCompanyByLocationId(device.locationId)?.id || '',
    locationId: device.locationId,
    active: device.active,
    lat: device.lat,
    lng: device.lng,
    altitude: device.altitude,
    periodicity: device.periodicity,
    groupId: device.groupId
  }
  showEditDialog.value = true
}

const openDeleteDialog = (device: Device) => {
  selectedDevice.value = device
  showDeleteDialog.value = true
}

const closeDialogs = () => {
  showCreateDialog.value = false
  showEditDialog.value = false
  showDeleteDialog.value = false
  selectedDevice.value = null
  resetForm()
}

const resetForm = () => {
  deviceForm.value = {
    code: '',
    name: '',
    extendedName: '',
    description: '',
    companyId: '',
    locationId: '',
    active: true,
    lat: 0,
    lng: 0,
    altitude: 0,
    periodicity: 30,
    groupId: 1
  }
}

// CRUD methods
const createDevice = async () => {
  // Validación básica
  if (!deviceForm.value.code || !deviceForm.value.name || !deviceForm.value.companyId || !deviceForm.value.locationId) {
    error.value = 'Por favor complete todos los campos requeridos'
    return
  }

  loading.value = true
  error.value = null
  
  try {
    // Generar slug desde el nombre
    const slug = deviceForm.value.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
    
    const newDevice = new Device({
      ...deviceForm.value,
      slug: slug,
      initDate: new Date(),
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: 'admin', // TODO: Get from auth store
      updatedBy: 'admin',
      alarmed: false,
      metadata: '{}'
    })
    
    console.log('Creating device:', newDevice.toCreateObject())
    await deviceStore.postDevice(newDevice)
    console.log('Device created successfully')
    await loadAllData() // Recargar todos los datos
    closeDialogs()
  } catch (err) {
    console.error('Error creating device:', err)
    error.value = err instanceof Error ? err.message : 'Error al crear dispositivo'
  } finally {
    loading.value = false
  }
}

const updateDevice = async () => {
  if (!selectedDevice.value) return

  // Validación básica
  if (!deviceForm.value.code || !deviceForm.value.name || !deviceForm.value.companyId || !deviceForm.value.locationId) {
    error.value = 'Por favor complete todos los campos requeridos'
    return
  }

  loading.value = true
  error.value = null
  
  try {
    // Generar slug desde el nombre
    const slug = deviceForm.value.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
    
    const updatedDevice = new Device({
      ...selectedDevice.value.toPlainObject(),
      ...deviceForm.value,
      slug: slug,
      updatedAt: new Date(),
      updatedBy: 'admin' // TODO: Get from auth store
    })
    
    console.log('Updating device:', updatedDevice)
    await deviceStore.putDevice(updatedDevice)
    console.log('Device updated successfully')
    await loadAllData() // Recargar todos los datos
    closeDialogs()
  } catch (err) {
    console.error('Error updating device:', err)
    error.value = err instanceof Error ? err.message : 'Error al actualizar dispositivo'
  } finally {
    loading.value = false
  }
}

const deleteDevice = async () => {
  if (!selectedDevice.value) return

  loading.value = true
  error.value = null
  
  try {
    await deviceStore.deleteDevice(selectedDevice.value.id)
    await loadAllData() // Recargar todos los datos
    closeDialogs()
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Error al eliminar dispositivo'
    console.error('Error al eliminar dispositivo:', err)
  } finally {
    loading.value = false
  }
}

// Load devices from API
const loadDevices = async () => {
  loading.value = true
  error.value = null
  
  try {
    await deviceStore.getDevices()
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Error al cargar dispositivos'
    console.error('Error al cargar dispositivos:', err)
  } finally {
    loading.value = false
  }
}

// Load companies from API
const loadCompanies = async () => {
  try {
    await userStore.getCompanies()
  } catch (err) {
    console.error('Error al cargar compañías:', err)
  }
}

// Load locations from API
const loadLocations = async () => {
  try {
    await locationsStore.getLocations()
  } catch (err) {
    console.error('Error al cargar ubicaciones:', err)
  }
}

// Load all data
const loadAllData = async () => {
  await Promise.all([
    loadDevices(),
    loadCompanies(),
    loadLocations()
  ])
}

const formatDate = (date: Date) => {
  // return date.toLocaleDateString('es-ES', {
  //   year: 'numeric',
  //   month: '2-digit',
  //   day: '2-digit'
  // })
  return date
}

// Lifecycle
onMounted(async () => {
  await loadAllData()
})
</script>

<template>
  <v-container fluid>
    <!-- Header Section -->
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center mb-6">
          <div>
            <h1 class="text-h4 font-weight-bold">Gestión de Dispositivos</h1>
            <p class="text-body-1 text-medium-emphasis">Administración de Fotorizotron y asignación a compañías</p>
          </div>
          <div>
            <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreateDialog">
              Nuevo Dispositivo
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Loading state -->
    <v-row v-if="loading" class="mb-6">
      <v-col cols="12" class="text-center">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        <p class="mt-4 text-body-1">Cargando dispositivos...</p>
      </v-col>
    </v-row>

    <!-- Error state -->
    <v-row v-else-if="error" class="mb-6">
      <v-col cols="12">
        <v-alert type="error" prominent>
          <v-alert-title>Error al cargar dispositivos</v-alert-title>
          {{ error }}
          <template v-slot:append>
            <v-btn color="white" variant="text" @click="loadDevices">
              Reintentar
            </v-btn>
          </template>
        </v-alert>
      </v-col>
    </v-row>

    <!-- Stats Cards -->
    <v-row v-else class="mb-6">
      <v-col cols="12" sm="6" md="3">
        <v-card flat elevation="2" class="rounded-lg">
          <v-card-text class="pb-0">
            <div class="d-flex align-center">
              <v-avatar color="#E8F5E8" size="48" class="mr-3">
                <v-icon color="#4CAF50" size="24">mdi-camera-outline</v-icon>
              </v-avatar>
              <div>
                <div class="text-caption text-medium-emphasis">Total Dispositivos</div>
                <div class="text-h4 font-weight-bold">{{ dashboardStats.totalDevices }}</div>
                <div class="text-caption text-success">En el sistema</div>
              </div>
            </div>
          </v-card-text>
          <v-card-text>
            <v-progress-linear model-value="100" height="6" color="#4CAF50" rounded></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card flat elevation="2" class="rounded-lg">
          <v-card-text class="pb-0">
            <div class="d-flex align-center">
              <v-avatar color="#E3F2FD" size="48" class="mr-3">
                <v-icon color="#2196F3" size="24">mdi-check-circle</v-icon>
              </v-avatar>
              <div>
                <div class="text-caption text-medium-emphasis">Dispositivos Activos</div>
                <div class="text-h4 font-weight-bold">{{ dashboardStats.activeDevices }}</div>
                <div class="text-caption text-info">{{ Math.round((dashboardStats.activeDevices/dashboardStats.totalDevices)*100) }}% operativos</div>
              </div>
            </div>
          </v-card-text>
          <v-card-text>
            <v-progress-linear 
              :model-value="(dashboardStats.activeDevices/dashboardStats.totalDevices)*100" 
              height="6" 
              color="#2196F3" 
              rounded
            ></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card flat elevation="2" class="rounded-lg">
          <v-card-text class="pb-0">
            <div class="d-flex align-center">
              <v-avatar color="#FFF3E0" size="48" class="mr-3">
                <v-icon color="#FF9800" size="24">mdi-alert-circle</v-icon>
              </v-avatar>
              <div>
                <div class="text-caption text-medium-emphasis">Con Alarmas</div>
                <div class="text-h4 font-weight-bold">{{ dashboardStats.alarmedDevices }}</div>
                <div class="text-caption" :class="dashboardStats.alarmedDevices > 0 ? 'text-warning' : 'text-success'">
                  {{ dashboardStats.alarmedDevices > 0 ? 'Requieren atención' : 'Todo normal' }}
                </div>
              </div>
            </div>
          </v-card-text>
          <v-card-text>
            <v-progress-linear 
              :model-value="dashboardStats.alarmedDevices > 0 ? (dashboardStats.alarmedDevices/dashboardStats.totalDevices)*100 : 5" 
              height="6" 
              color="#FF9800" 
              rounded
            ></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card flat elevation="2" class="rounded-lg">
          <v-card-text class="pb-0">
            <div class="d-flex align-center">
              <v-avatar color="#F3E5F5" size="48" class="mr-3">
                <v-icon color="#9C27B0" size="24">mdi-domain</v-icon>
              </v-avatar>
              <div>
                <div class="text-caption text-medium-emphasis">Compañías Activas</div>
                <div class="text-h4 font-weight-bold">{{ dashboardStats.companiesWithDevices }}</div>
                <div class="text-caption text-purple">Con dispositivos asignados</div>
              </div>
            </div>
          </v-card-text>
          <v-card-text>
            <v-progress-linear 
              :model-value="(dashboardStats.companiesWithDevices/companies.length)*100" 
              height="6" 
              color="#9C27B0" 
              rounded
            ></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters and Search -->
    <v-row>
      <v-col cols="12">
        <v-card flat elevation="2" class="rounded-lg">
          <v-card-text>
            <v-row>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="searchQuery"
                  label="Buscar dispositivos"
                  prepend-inner-icon="mdi-magnify"
                  variant="outlined"
                  density="comfortable"
                  hide-details
                  clearable
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="3">
                <v-select
                  v-model="filterCompany"
                  :items="[
                    { title: 'Todas las compañías', value: 'all' },
                    ...companies.map(c => ({ title: c.name, value: c.id }))
                  ]"
                  label="Compañía"
                  variant="outlined"
                  density="comfortable"
                  hide-details
                ></v-select>
              </v-col>

              <v-col cols="12" md="3">
                <v-select
                  v-model="filterStatus"
                  :items="[
                    { title: 'Todos los estados', value: 'all' },
                    { title: 'Activos', value: 'active' },
                    { title: 'Inactivos', value: 'inactive' },
                    { title: 'Con alarmas', value: 'alarmed' }
                  ]"
                  label="Estado"
                  variant="outlined"
                  density="comfortable"
                  hide-details
                ></v-select>
              </v-col>

              <v-col cols="12" md="3">
                <v-select
                  v-model="itemsPerPage"
                  :items="[5, 10, 25, 50]"
                  label="Elementos por página"
                  variant="outlined"
                  density="comfortable"
                  hide-details
                ></v-select>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Devices Table -->
    <v-row class="mt-6">
      <v-col cols="12">
        <v-card flat elevation="2" class="rounded-lg">
          <v-card-title class="d-flex justify-space-between align-center px-6 py-4">
            <div class="text-h6 font-weight-bold">
              Dispositivos Fotorizotron
              <v-chip size="small" color="primary" class="ml-2">
                {{ filteredDevices.length }}
              </v-chip>
            </div>
          </v-card-title>

          <v-card-text class="px-0">
            <!-- Loading state for table -->
            <div v-if="loading" class="text-center pa-8">
              <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
              <p class="mt-4 text-body-1">Cargando dispositivos...</p>
            </div>

            <!-- Empty state -->
            <div v-else-if="devices.length === 0" class="text-center pa-8">
              <v-icon size="64" color="grey-lighten-1">mdi-camera-outline</v-icon>
              <p class="mt-4 text-h6 text-grey">No hay dispositivos registrados</p>
              <p class="text-body-2 text-grey">Los dispositivos aparecerán aquí una vez que se registren en el sistema.</p>
            </div>

            <!-- Table with data -->
            <v-table v-else>
              <thead>
                <tr>
                  <th class="text-left px-6">ID</th>
                  <th class="text-left">Nombre</th>
                  <th class="text-left">Compañía</th>
                  <th class="text-left">Ubicación</th>
                  <th class="text-left">Estado</th>
                  <th class="text-left">Periodicidad</th>
                  <th class="text-left">Creado</th>
                  <th class="text-left">Acciones</th>
                </tr>
              </thead>
              <tbody>
                <tr 
                  v-for="device in paginatedDevices" 
                  :key="device.id"
                  class="device-row"
                  :class="{ 'alarmed-device': device.alarmed }"
                >
                  <td class="px-6">
                    <div class="d-flex align-center">
                      <v-icon 
                        :icon="getStatusIcon(device)"
                        :color="getStatusColor(device)"
                        size="16"
                        class="mr-2"
                      ></v-icon>
                      <span class="font-family-monospace font-weight-bold">{{ device.id }}</span>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div class="font-weight-medium">{{ device.name }}</div>
                      <div class="text-caption text-medium-emphasis">{{ device.extendedName }}</div>
                    </div>
                  </td>
                  <td>
                    <div class="font-weight-medium">{{ getCompanyByLocationId(device.locationId)?.name || 'No asignada' }}</div>
                  </td>
                  <td>
                    <div>{{ getLocationById(device.locationId)?.name || 'No especificada' }}</div>
                    <div class="text-caption text-medium-emphasis">
                      {{ device.lat.toFixed(4) }}, {{ device.lng.toFixed(4) }}
                    </div>
                  </td>
                  <td>
                    <v-chip 
                      :color="getStatusColor(device)"
                      size="small"
                      text-color="white"
                    >
                      {{ getStatusText(device) }}
                    </v-chip>
                  </td>
                  <td>
                    <div class="d-flex align-center">
                      <v-icon size="16" class="mr-1">mdi-clock-outline</v-icon>
                      <span>{{ device.periodicity }}min</span>
                    </div>
                  </td>
                  <td>
                    <div class="text-body-2">{{ formatDate(device.createdAt) }}</div>
                    <div class="text-caption text-medium-emphasis">{{ device.createdBy }}</div>
                  </td>
                  <td>
                    <div class="d-flex gap-1">
                      <v-btn
                        icon
                        size="small"
                        color="primary"
                        variant="text"
                        @click="openEditDialog(device)"
                      >
                        <v-icon size="18">mdi-pencil</v-icon>
                        <v-tooltip activator="parent" location="top">Editar dispositivo</v-tooltip>
                      </v-btn>

                      <v-btn
                        icon
                        size="small"
                        color="error"
                        variant="text"
                        @click="openDeleteDialog(device)"
                      >
                        <v-icon size="18">mdi-delete</v-icon>
                        <v-tooltip activator="parent" location="top">Eliminar dispositivo</v-tooltip>
                      </v-btn>
                    </div>
                  </td>
                </tr>
              </tbody>
            </v-table>

            <!-- Pagination -->
            <div class="d-flex justify-center align-center pa-4" v-if="totalPages > 1">
              <v-pagination
                v-model="currentPage"
                :length="totalPages"
                :total-visible="7"
                color="primary"
              ></v-pagination>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Create Dialog -->
    <v-dialog v-model="showCreateDialog" max-width="900" persistent>
      <v-card>
        <v-card-title class="text-h5 px-6 py-4 bg-primary text-white">
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          Crear Nuevo Dispositivo Fotorizotron
        </v-card-title>

        <v-card-text class="pa-6">
          <v-form>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="deviceForm.code"
                  label="Código del Dispositivo"
                  variant="outlined"
                  density="comfortable"
                  :rules="[v => !!v || 'Campo requerido']"
                  placeholder="ej: FT-001"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="deviceForm.name"
                  label="Nombre del Dispositivo"
                  variant="outlined"
                  density="comfortable"
                  :rules="[v => !!v || 'Campo requerido']"
                  placeholder="ej: Fotorizotron Norte"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="deviceForm.extendedName"
                  label="Nombre Extendido"
                  variant="outlined"
                  density="comfortable"
                  placeholder="ej: Fotorizotron - Análisis Radicular Norte"
                ></v-text-field>
              </v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="deviceForm.description"
                  label="Descripción"
                  variant="outlined"
                  rows="3"
                  placeholder="Describe las características y propósito del dispositivo..."
                ></v-textarea>
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="deviceForm.companyId"
                  :items="companies.map(c => ({ title: c.name, value: c.id }))"
                  label="Compañía"
                  variant="outlined"
                  density="comfortable"
                  :rules="[v => !!v || 'Campo requerido']"
                ></v-select>
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="deviceForm.locationId"
                  :items="availableLocations.map(l => ({ title: l.name, value: l.id }))"
                  label="Ubicación"
                  variant="outlined"
                  density="comfortable"
                  :rules="[v => !!v || 'Campo requerido']"
                  :disabled="!deviceForm.companyId"
                ></v-select>
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="deviceForm.lat"
                  label="Latitud"
                  type="number"
                  step="0.000001"
                  variant="outlined"
                  density="comfortable"
                  :rules="[v => v >= -90 && v <= 90 || 'Debe estar entre -90 y 90']"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="deviceForm.lng"
                  label="Longitud"
                  type="number"
                  step="0.000001"
                  variant="outlined"
                  density="comfortable"
                  :rules="[v => v >= -180 && v <= 180 || 'Debe estar entre -180 y 180']"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="deviceForm.altitude"
                  label="Altitud (msnm)"
                  type="number"
                  variant="outlined"
                  density="comfortable"
                  suffix="metros"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="deviceForm.periodicity"
                  label="Periodicidad (minutos)"
                  type="number"
                  min="1"
                  variant="outlined"
                  density="comfortable"
                  suffix="min"
                  :rules="[v => v > 0 || 'Debe ser mayor a 0']"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="deviceForm.groupId"
                  label="ID de Grupo"
                  type="number"
                  min="1"
                  variant="outlined"
                  density="comfortable"
                  :rules="[v => v > 0 || 'Debe ser mayor a 0']"
                ></v-text-field>
              </v-col>

              <v-col cols="12">
                <v-checkbox
                  v-model="deviceForm.active"
                  label="Dispositivo activo al crear"
                  color="success"
                ></v-checkbox>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeDialogs">
            Cancelar
          </v-btn>
          <v-btn color="primary" @click="createDevice">
            Crear Dispositivo
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Dialog -->
    <v-dialog v-model="showEditDialog" max-width="900" persistent>
      <v-card v-if="selectedDevice">
        <v-card-title class="text-h5 px-6 py-4 bg-info text-white">
          <v-icon class="mr-2">mdi-pencil</v-icon>
          Editar Dispositivo: {{ selectedDevice.name }}
        </v-card-title>

        <v-card-text class="pa-6">
          <v-form>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="deviceForm.code"
                  label="Código del Dispositivo"
                  variant="outlined"
                  density="comfortable"
                  :rules="[v => !!v || 'Campo requerido']"
                  placeholder="ej: FT-001"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="deviceForm.name"
                  label="Nombre del Dispositivo"
                  variant="outlined"
                  density="comfortable"
                  :rules="[v => !!v || 'Campo requerido']"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="deviceForm.extendedName"
                  label="Nombre Extendido"
                  variant="outlined"
                  density="comfortable"
                ></v-text-field>
              </v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="deviceForm.description"
                  label="Descripción"
                  variant="outlined"
                  rows="3"
                ></v-textarea>
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="deviceForm.companyId"
                  :items="companies.map(c => ({ title: c.name, value: c.id }))"
                  label="Compañía"
                  variant="outlined"
                  density="comfortable"
                  :rules="[v => !!v || 'Campo requerido']"
                ></v-select>
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="deviceForm.locationId"
                  :items="availableLocations.map(l => ({ title: l.name, value: l.id }))"
                  label="Ubicación"
                  variant="outlined"
                  density="comfortable"
                  :rules="[v => !!v || 'Campo requerido']"
                ></v-select>
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="deviceForm.lat"
                  label="Latitud"
                  type="number"
                  step="0.000001"
                  variant="outlined"
                  density="comfortable"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="deviceForm.lng"
                  label="Longitud"
                  type="number"
                  step="0.000001"
                  variant="outlined"
                  density="comfortable"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="deviceForm.altitude"
                  label="Altitud (msnm)"
                  type="number"
                  variant="outlined"
                  density="comfortable"
                  suffix="metros"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="deviceForm.periodicity"
                  label="Periodicidad (minutos)"
                  type="number"
                  min="1"
                  variant="outlined"
                  density="comfortable"
                  suffix="min"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="deviceForm.groupId"
                  label="ID de Grupo"
                  type="number"
                  min="1"
                  variant="outlined"
                  density="comfortable"
                ></v-text-field>
              </v-col>

              <v-col cols="12">
                <v-checkbox
                  v-model="deviceForm.active"
                  label="Dispositivo activo"
                  color="success"
                ></v-checkbox>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeDialogs">
            Cancelar
          </v-btn>
          <v-btn color="primary" @click="updateDevice">
            Guardar Cambios
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="500">
      <v-card v-if="selectedDevice">
        <v-card-title class="text-h5 px-6 py-4 bg-error text-white">
          <v-icon class="mr-2">mdi-delete</v-icon>
          Confirmar Eliminación
        </v-card-title>

        <v-card-text class="pa-6">
          <v-alert type="warning" variant="tonal" class="mb-4">
            Esta acción no se puede deshacer. Se eliminará permanentemente el dispositivo y todos sus datos asociados.
          </v-alert>

          <p class="text-body-1">
            ¿Estás seguro de que deseas eliminar el dispositivo <strong>{{ selectedDevice.name }}</strong>?
          </p>

          <div class="mt-4 pa-3 bg-grey-lighten-5 rounded">
            <div class="text-body-2"><strong>ID:</strong> {{ selectedDevice.id }}</div>
            <div class="text-body-2"><strong>Ubicación:</strong> {{ getLocationById(selectedDevice.locationId)?.name }}</div>
            <div class="text-body-2"><strong>Compañía:</strong> {{ getCompanyByLocationId(selectedDevice.locationId)?.name }}</div>
          </div>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeDialogs">
            Cancelar
          </v-btn>
          <v-btn color="error" @click="deleteDevice">
            Eliminar Dispositivo
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<style scoped>
.device-row {
  transition: background-color 0.2s ease;
}

.device-row:hover {
  background-color: #f5f5f5;
}

.device-row.alarmed-device {
  border-left: 4px solid #ff9800;
  background-color: #fff8e1;
}

.device-row.alarmed-device:hover {
  background-color: #ffecb3;
}

.v-card {
  transition: box-shadow 0.3s ease;
}

.v-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08) !important;
}
</style>
